import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { DeliveryRunInspector } from '~/features/deliveries/components/run-inspector/delivery-run-inspector';

import { useBatchHeader } from '../../hooks/use-batch-detail';

import { DeliveryWizardProgressStep } from './delivery-wizard-progress-step';

interface Props {
  /** Catalog-ops batch id — its header carries `deliveryJobId` once delivery starts. */
  batchId: string;
  /** `scheduled-job-…` slug for the pre-run progress poll (same as the member view). */
  jobId: string;
  onBackToBatch: () => void;
  /** A run started from this wizard (Retry delivery) — shown instead of the batch's own run. */
  runId?: string | null;
}

/**
 * Step 3 for subsidiary admins and super-admins: the Temporal-style
 * `DeliveryRunInspector` for this batch's delivery run. Until the header
 * reports the run id (`deliveryJobId`, polled every 5 s after approval) it
 * shows the member progress view so the step is never blank. Unlike members,
 * admins are NOT redirected on completion — they usually want to inspect the
 * outcome — so a "Back to batch" action is offered instead. Retry actions
 * follow the new run in place (the console route is super-admin only).
 */
export function DeliveryWizardRunStep({
  batchId,
  jobId,
  onBackToBatch,
  runId: startedRunId = null,
}: Props) {
  const { t } = useTranslation('common');
  const { data: header } = useBatchHeader(batchId, { pollForDeliveryRun: true });
  // A run picked from the retry chain / created by a retry overrides the batch's own.
  const [followedRunId, setFollowedRunId] = useState<string | null>(null);
  const runId = followedRunId ?? startedRunId ?? header?.deliveryJobId ?? null;

  return (
    <div
      className="flex w-full flex-1 flex-col gap-4 py-2"
      data-testid="delivery-wizard-step-panel-progress"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          className="text-foreground text-xl font-semibold tracking-tight"
          data-testid="delivery-wizard-run-title"
        >
          {t('deliveryWizard.runStep.title')}
        </h2>
        <div className="flex items-center gap-2">
          {followedRunId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => {
                setFollowedRunId(null);
              }}
              data-testid="delivery-wizard-run-back-to-batch-run-button"
            >
              {t('deliveryWizard.runStep.backToBatchRun')}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="cursor-pointer gap-1.5"
            onClick={onBackToBatch}
            data-testid="delivery-wizard-run-back-to-batch-button"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            {t('deliveryWizard.runStep.backToBatch')}
          </Button>
        </div>
      </div>

      {runId ? (
        <DeliveryRunInspector
          key={runId}
          runId={runId}
          onRunChange={setFollowedRunId}
          batchId={batchId}
          testId="delivery-wizard-run-inspector"
        />
      ) : (
        <div className="space-y-2" data-testid="delivery-wizard-run-waiting">
          <p className="text-muted-foreground text-sm">
            {t('deliveryWizard.runStep.waitingForRun')}
          </p>
          <DeliveryWizardProgressStep
            testId="delivery-wizard-run-progress"
            jobId={jobId}
            onComplete={() => {
              // No auto-redirect for admins — the inspector takes over once the run id lands.
            }}
          />
        </div>
      )}
    </div>
  );
}
